import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { GiFairyWings } from "react-icons/gi";
import { 
  FaRegCalendarAlt, 
  FaImage, 
  FaMapMarkerAlt, 
  FaArrowRight, 
  FaRegNewspaper 
} from "react-icons/fa"; 
import "./NewsAndEvent.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

// Basic Date Formatter for News
const formatCardDate = (dateString) => {
  return new Date(dateString).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};
const getMonthString = (dateString) => new Date(dateString).toLocaleString('en-US', { month: 'short' }).toUpperCase();
const getDayString = (dateString) => new Date(dateString).getDate().toString().padStart(2, '0');

// Smart Time Formatter
const formatTimeAmPm = (time24) => {
    if (!time24) return '';
    const [h, m] = time24.split(':');
    const hours = parseInt(h, 10);
    const suffix = hours >= 12 ? 'PM' : 'AM';
    const hr12 = hours % 12 || 12;
    return `${hr12.toString().padStart(2, '0')}:${m} ${suffix}`;
};

// Smart Date & Time Formatter (Brings over complete Event details)
const formatEventDateTime = (start, end, sTime, eTime) => {
    if (!start) return '';
    
    const startDateStr = new Date(start).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    const isSameDay = !end || (start.split('T')[0] === end.split('T')[0]);
    
    const startTimeStr = sTime ? formatTimeAmPm(sTime) : '';
    const endTimeStr = eTime ? formatTimeAmPm(eTime) : '';

    if (isSameDay) {
        // Single Date Logic
        let timeStr = '';
        if (startTimeStr && endTimeStr) {
            timeStr = ` (Start Time: ${startTimeStr} - End Time: ${endTimeStr})`;
        } else if (startTimeStr) {
            timeStr = ` (Start Time: ${startTimeStr})`;
        } else if (endTimeStr) {
            timeStr = ` (End Time: ${endTimeStr})`;
        }
        return `${startDateStr}${timeStr}`;
    } else {
        // Date Range Logic
        const endDateStr = new Date(end).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
        const startPart = startTimeStr ? `${startDateStr}, ${startTimeStr}` : startDateStr;
        const endPart = endTimeStr ? `${endDateStr}, ${endTimeStr}` : endDateStr;
        return `${startPart} to ${endPart}`;
    }
};

function NewsAndEvent() {
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNewsAndEvents = async () => {
      try {
        const [newsRes, eventsRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/news/public`),
          fetch(`${API_BASE_URL}/api/events/public`)
        ]);

        const newsData = await newsRes.json();
        const eventsData = await eventsRes.json();

        // Sort both by date descending
        const sortedNews = Array.isArray(newsData) ? newsData.sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate)) : [];
        const sortedEvents = Array.isArray(eventsData) ? eventsData.sort((a, b) => new Date(b.eventDate) - new Date(a.eventDate)) : [];

        setNews(sortedNews);
        setEvents(sortedEvents);
      } catch (error) {
        console.error("Failed to fetch news/events:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchNewsAndEvents();
  }, []);

  let displayItems = [];

  // 1. Cap News at 3 if events exist, otherwise show up to 4 News.
  const maxNewsDesired = events.length > 0 ? 3 : 4;
  const newsToTake = Math.min(news.length, maxNewsDesired);
  
  // Add News first (LEFT)
  news.slice(0, newsToTake).forEach(n => {
    displayItems.push({ ...n, dataType: 'news' });
  });

  // 2. Calculate remaining slots out of 4 max
  const slotsForEvents = 4 - displayItems.length;
  const eventsToTake = Math.min(events.length, slotsForEvents);

  // Add remaining Events (RIGHT)
  events.slice(0, eventsToTake).forEach(e => {
    displayItems.push({ ...e, dataType: 'event' });
  });

  // === LOADING STATE ===
  if (loading) {
    return (
      <div className="home-news-events-wrapper">
        <div className="home-news-events-container">
          <div className="home-news-events-loading-state">
            <div className="home-news-events-spinner"></div>
            <p>Loading Latest Updates...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="home-news-events-wrapper">
      <div className="home-news-events-container">
        
        {/* HEADER */}
        <div className="home-news-events-header">
          <h2 className="home-news-events-title">News and Events</h2>
          <div className="home-news-events-divider">
            <span className="home-news-events-line"></span>
            <GiFairyWings className="home-news-events-icon" />
            <span className="home-news-events-line"></span>
          </div>
        </div>

        {/* CONTENT FLEXBOX / GRID */}
        <div className="home-news-events-flex-grid">
          {displayItems.length > 0 ? (
            displayItems.map((item, index) => {
              
              // === EVENT CARD ===
              if (item.dataType === 'event') {
                return (
                  <Link to={`/events/${item.slug}`} key={`evt-${item._id || index}`} className="home-news-events-card">
                    <div className="home-news-events-card-top-event">
                      <div className="home-news-events-date-box">
                        <span className="home-news-events-month">{getMonthString(item.eventDate)}</span>
                        <span className="home-news-events-day">{getDayString(item.eventDate)}</span>
                      </div>
                    </div>
                    <div className="home-news-events-content">
                      <h3 className="home-news-events-card-title">{item.title}</h3>
                      <div className="home-news-events-meta-list">
                        <div className="home-news-events-meta">
                          <FaRegCalendarAlt className="home-news-events-meta-icon" />
                          {/* Updated to display full Date Range, Start Time, and End Time details */}
                          <span>{formatEventDateTime(item.eventDate, item.endDate, item.startTime, item.endTime)}</span>
                        </div>
                        <div className="home-news-events-meta">
                          <FaMapMarkerAlt className="home-news-events-meta-icon" />
                          <span>{item.eventPlace || "College Campus"}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              }

              // === NEWS CARD ===
              return (
                <Link to={`/news/${item.slug}`} key={`nws-${item._id || index}`} className="home-news-events-card">
                  <div className="home-news-events-image-wrapper">
                    {item.image ? (
                      <img src={`${API_BASE_URL}${item.image}`} alt={item.title} className="home-news-events-image" />
                    ) : (
                      <div className="home-news-events-no-image">
                        <FaImage size={30} />
                      </div>
                    )}
                  </div>
                  <div className="home-news-events-content">
                    <h3 className="home-news-events-card-title">{item.title}</h3>
                    <div className="home-news-events-date">
                      <FaRegCalendarAlt className="home-news-events-meta-icon" /> 
                      <span>{formatCardDate(item.publishDate)}</span>
                    </div>
                  </div>
                </Link>
              );

            })
          ) : (
            // === EMPTY STATE ===
            <div className="home-news-events-empty-state">
              <div className="home-news-events-empty-icon-box">
                <FaRegNewspaper className="home-news-events-empty-icon" />
              </div>
              <h3 className="home-news-events-empty-title">Stay Tuned!</h3>
              <p className="home-news-events-empty-text">
                Currently, there are no news or upcoming events to display. <br />
                Please check back later for updates on our latest initiatives.
              </p>
            </div>
          )}
        </div>

        {/* VIEW MORE BUTTONS */}
        {(news.length > 0 || events.length > 0) && (
          <div className="home-news-events-actions">
            {news.length > 0 && (
              <Link to="/news" className="home-news-events-btn btn-outline">
                View All News <FaArrowRight />
              </Link>
            )}
            {events.length > 0 && (
              <Link to="/events" className="home-news-events-btn btn-solid">
                View All Events <FaArrowRight />
              </Link>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

export default NewsAndEvent;